import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { API_URL } from "../config";

const WhatsAppConnection = () => {
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessCategory, setBusinessCategory] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleConnect = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_URL}/api/whatsapp/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          phone_number: phoneNumber,
          business_name: businessName,
          business_category: businessCategory,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error connecting to WhatsApp');
      }

      const data = await response.json();
      setConnectionStatus('waiting');
      // Handle QR code display here
    } catch (err) {
      console.error('Connection error:', err);
      setError(err.message || 'Error connecting to WhatsApp');
      setConnectionStatus('disconnected');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_URL}/api/whatsapp/disconnect`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error disconnecting from WhatsApp');
      }

      setConnectionStatus('disconnected');
    } catch (err) {
      console.error('Disconnection error:', err);
      setError(err.message || 'Error disconnecting from WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  const handleWebhookUpdate = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_URL}/api/whatsapp/webhook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          url: webhookUrl,
          secret: webhookSecret,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error updating webhook settings');
      }

      // Show success message or update UI
    } catch (err) {
      console.error('Webhook update error:', err);
      setError(err.message || 'Error updating webhook settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Configuración de WhatsApp</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Estado de la Conexión</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-600">Estado actual: {connectionStatus}</p>
            {connectionStatus === 'waiting' && (
              <p className="text-yellow-600 mt-2">Escanea el código QR con WhatsApp</p>
            )}
          </div>
          {connectionStatus === 'disconnected' ? (
            <button
              onClick={handleConnect}
              disabled={loading}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50"
            >
              {loading ? 'Conectando...' : 'Conectar WhatsApp'}
            </button>
          ) : (
            <button
              onClick={handleDisconnect}
              disabled={loading}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 disabled:opacity-50"
            >
              {loading ? 'Desconectando...' : 'Desconectar'}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Configuración del Negocio</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-700 mb-2">Número de WhatsApp</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="+52 123 456 7890"
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2">Nombre del Negocio</label>
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="Mi Negocio"
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2">Categoría del Negocio</label>
            <select
              value={businessCategory}
              onChange={(e) => setBusinessCategory(e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="">Selecciona una categoría</option>
              <option value="retail">Retail</option>
              <option value="restaurant">Restaurante</option>
              <option value="service">Servicios</option>
              <option value="other">Otro</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Configuración de Webhook</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 mb-2">URL del Webhook</label>
            <input
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="https://tu-dominio.com/webhook"
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2">Secreto del Webhook</label>
            <input
              type="password"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="Tu secreto aquí"
            />
          </div>
          <button
            onClick={handleWebhookUpdate}
            disabled={loading}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {loading ? 'Actualizando...' : 'Actualizar Webhook'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppConnection; 